import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Fab,
	Grid,
	MenuItem,
	Pagination,
	Select,
	Skeleton,
	Stack,
	Typography,
	Zoom,
	useMediaQuery
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import strings from '../../Globalization';
import consts from '../../consts';
import { useUniqueEffect } from '../../hooks';
import util from '../../util';
import { getList, getRecord } from '../crud-helper';

const sortRegex = /(\w+)(ASC|DESC)?/gi; // Valid defaultSort Example = descendingSortColumn DESC, ascendingSortColumn ASC
const convertDefaultSort = (defaultSort) => {
	const orderBy = [];
	if (typeof defaultSort === 'string') {
		const sortFields = defaultSort.split(',');
		for (const sortField of sortFields) {
			const sortInfo = sortField.match(sortRegex);
			if (sortInfo?.length) {
				const [field, direction = 'ASC'] = sortInfo;
				orderBy.push({ field: field.trim(), sort: direction?.trim().toLowerCase() });
			}
		}
	}
	return orderBy;
};


export const Bull = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'active' && prop === 'onClick'
})(({ theme, active }) => ({
	width: theme.spacing(),
	height: theme.spacing(),
	borderRadius: theme.spacing(),
	transition: theme.transitions.create(['width', 'heght'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen
	}),
	backgroundColor: theme.palette.text.disabled,
	...(active && {
		backgroundColor: theme.palette.primary.main,
		width: theme.spacing(1.5),
		height: theme.spacing(1.5)
	})
}));
export const CarouselFab = styled(Fab)(({ theme }) => ({
	minWidth: theme.spacing(4),
	minHeight: theme.spacing(4),
	width: theme.spacing(4),
	height: theme.spacing(4),
}));

export const GridListPagination = ({
	page = 1,
	pageCount = 1,
	pageSize = 10,
	rowCount = 0,
	loading,
	onChangePage,
	onChangePageSize,
	pageSizeOptions = [15, 25, 50]
}) => {
	const startRow = rowCount === 0 || page > pageCount ? 0 : page * pageSize + 1;
	const endRow = Math.min((page + 1) * pageSize, rowCount);

	return (
		<Grid rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} container alignItems="center">
			<Grid size="grow" flex={1} container alignItems="center">
				{strings.Page || 'Page'} <Pagination count={pageCount} page={page + 1} onChange={onChangePage} /> of{' '}
				{pageCount}
			</Grid>
			<Grid size="grow" flex={1}>
				<Select value={pageSize} displayEmpty variant="standard" disableUnderline={true} onChange={onChangePageSize}>
					{pageSizeOptions.map((pageSizeOption, index) => (
						<MenuItem key={`page-size-option-${pageSizeOption}`} value={pageSizeOption}>
							{pageSizeOption}
						</MenuItem>
					))}
				</Select>
				{strings.RecordsPerPage}
			</Grid>
			<Grid size="auto">
				<div>
					{strings.Displaying} {startRow} - {endRow} of {rowCount} {strings.Records}
				</div>
			</Grid>
		</Grid>
	);
};

const Item = ({ data, columns, onClick }) => {
	return (
		<Card onClick={onClick}>
			<CardContent>
				<Grid size="grow" container>
					{columns.map((column, i) => (
						<Grid size="grow" container key={`column-${i}`}>
							<Grid size={5}>
								<Typography gutterBottom variant="body2" color="textSecondary" component="span">
									{column.headerName}
								</Typography>
							</Grid>
							<Grid size={7}>
								<Typography variant="body2" gutterBottom color="textSecondary" component="span">
									{typeof data[column.field] === 'string'
										? data[column.field]
										: JSON.stringify(data[column.field])}
								</Typography>
							</Grid>
						</Grid>
					))}
				</Grid>
			</CardContent>
		</Card>
	);
};

export const DefaultPlaceholder = (props) => (
	<Skeleton
		variant="rectangular"
		width={'100%'}
		height={'100%'}
		sx={{ minHeight: '20vh', borderRadius: (theme) => `${theme.shape?.borderRadius || 0}px` }}
		{...props}
	/>
);
export const Masonry = ({
	rows,
	columns,
	onClickItem,
	noOfitemsPerRow,
	component: Component,
	placeholder: PlaceholderComponent,
	page,
	loading,
	rowProps = { rowSpacing: 1, columnSpacing: { xs: 1, sm: 1, md: 2 }, sx: { padding: 1 } },
	...rest
}) => {
	const theme = useTheme();
	const itemColumnSize = Math.ceil(12 / noOfitemsPerRow);
	const sliceArrayIntoChunks = (arr, chunkSize) => {
		const chunks = [];
		if (Array.isArray(arr) && chunkSize > 0) {
			for (let i = 0; i < arr.length; i += chunkSize) {
				const chunk = arr.slice(i, i + chunkSize);
				chunks.push(chunk);
			}
		}

		return chunks;
	};
	const dataRowsChunks = useMemo(() => sliceArrayIntoChunks(rows, noOfitemsPerRow), [noOfitemsPerRow, rows]);
	return (
		<Grid {...rest} container size="grow">
			{!loading &&
				dataRowsChunks.length > 0 &&
				dataRowsChunks.map((rowEntries, rowIndex) => (
					<Grid {...rowProps} container size="grow" key={`row-${rowIndex}`}>
						{rowEntries.map((rowEntry, columnIndex) => (
							<Grid
								size={{ xs: 12, lg: itemColumnSize }}
								key={`row-${rowIndex}-item-${columnIndex}`}
								onClick={onClickItem}
							>
								<Component data={rowEntry} columns={columns} />
							</Grid>
						))}
					</Grid>
				))}
			{loading && (
				<Grid container size="grow" {...rowProps}>
					{Array(noOfitemsPerRow)
						.fill('placeholder')
						.map((pl, i) => (
							<Grid
								key={i}
								size={{ xs: 12, lg: itemColumnSize }}
								sx={{ display: 'flex', alignItems: 'center', padding: theme.spacing(2) }}
							>
								<PlaceholderComponent />
							</Grid>
						))}
				</Grid>
			)}
		</Grid>
	);
};

export const useDidUpdate = (effect, deps) => {
	const depsRef = useRef(deps);
	const updated = !util.deepEqual(deps, depsRef.current);
	useEffect(() => {
		if (updated) {
			effect();
			depsRef.current = deps;
		}

	}, [updated]);
};
const useCarouselControls = (ref, { pagination: rpp = 0, count = 0, onEndReached, columnGap = 0, onChange }) => {
	const threshold = 24;
	const swipeSpeed = 1; // sec;
	const theme = useTheme();
	const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
	const pagination = isMdDown ? 1 : rpp;
	const cursors = pagination > 0 && count > 0 ? Math.ceil(count / pagination) : 0;
	const itemWidthRef = useRef(0);

	const cursorRef = useRef(1);
	const coordsRef = useRef({ touchStart: { x: 0, y: 0, time: Date.now() }, mouseStart: { x: 0, y: 0, time: Date.now() } });
	const isDraggingRef = useRef(false);
	const goToPage = useCallback(
		(page) => {
			if (!!ref.current && page >= 1 && page <= cursors) {
				const pageWidth = ref.current?.clientWidth;
				const pageIndex = page - 1;
				const columnGapTotalWidth = pageIndex > 0 ? columnGap * (pagination - 1) * page : 0;
				const scrollTo = pageWidth * pageIndex + columnGapTotalWidth;
				onChange(page);
				cursorRef.current = page;
				ref.current.scrollLeft = scrollTo;
			}
		},
		[pagination, cursors, onChange]
	);

	const goBack = useCallback(() => {
		if (!!ref.current && cursorRef.current > 1) {
			const page = cursorRef.current - 1;
			goToPage(page);

		}
	}, [pagination, cursors, onChange]);

	const goForward = useCallback(() => {
		if (!!ref.current && cursorRef.current < cursors) {
			const page = cursorRef.current + 1;
			goToPage(page);
		}
	}, [pagination, cursors]);



	const applyChildWidth = useCallback(
		(reset = false) => {
			const noOfChildren = ref.current?.children?.length;
			if (noOfChildren > 0) {
				let parentClientWidth = ref.current.clientWidth;
				const totalColumnGap = (pagination - 1) * (columnGap ?? 0);
				parentClientWidth = parentClientWidth - totalColumnGap;
				itemWidthRef.current = Math.ceil(parentClientWidth / pagination);
				Array.from(ref.current?.children).forEach((child, i) => {
					child.dataset.carouselItem = i + 1;
					if (i === 0 || (i > 0 && i % pagination === 0)) {
						child.style.scrollSnapAlign = 'start';
					}
				});
			}
		},
		[count, pagination, columnGap]
	);

	const applyParentStyles = useCallback(() => {
		if (ref.current) {
			let totalWidth = ref.current?.clientWidth;
			let childWidth = Math.ceil(totalWidth / pagination);
			const columnGapPercentage = ((columnGap ?? 0) / (totalWidth ?? 1)) * 100
			const gridTemplateColumnsWidthStr = (1 / pagination).toFixed(3) * 100 - columnGapPercentage;
			let gridTemplateColumns = '';
			for (let i = 0; i < count; i++) {
				gridTemplateColumns = `${gridTemplateColumns} ${gridTemplateColumnsWidthStr}%`;
			}
			gridTemplateColumns = gridTemplateColumns.trim();
			ref.current.style.overflow = 'hidden';
			ref.current.style.columnGap = `${childWidth}px`;
			ref.current.style.columnGap = `${columnGap}px`;
			ref.current.style.scrollSnapType = 'x mandatory';
			ref.current.style.scrollBehavior = 'smooth';
			ref.current.style.scrollSnapPointsX = `repeat(${totalWidth}px)`;
			ref.current.style.display = 'grid';
			ref.current.style.gridTemplateColumns = gridTemplateColumns;
			ref.current.style.gridTemplateRows = '1fr';
			ref.current.style.gridAutoFlow = 'column';
		}
	}, [pagination, columnGap, count]);

	const handleTouchStart = (e) => {
		coordsRef.current.touchStart.x = e.targetTouches[0].clientX;
		coordsRef.current.touchStart.y = e.targetTouches[0].clientY;
		coordsRef.current.touchStart.time = Date.now();
	};
	const handleTouchEnd = (e) => {
		const touchEndX = e.changedTouches[0].clientX;
		const touchEndY = e.changedTouches[0].clientY;
		const touchStartX = coordsRef.current.touchStart.x;
		const touchStartY = coordsRef.current.touchStart.y;
		const elapsedTime = (Date.now() - coordsRef.current.touchStart.time) / 1000;
		if (elapsedTime > swipeSpeed) {
			return;
		}
		const xDistance = touchStartX - touchEndX;
		const yDistance = touchStartY - touchEndY;

		if (Math.abs(xDistance) < threshold && Math.abs(yDistance) < threshold) {
			return;
		}

		if (Math.abs(xDistance) >= Math.abs(yDistance)) {
			xDistance > 0 ? goForward() : goBack();
		} else {
			yDistance > 0 ? goForward() : goBack();
		}
	};

	const handleMousedown = (e) => {
		isDraggingRef.current = true;
		coordsRef.current.mouseStart.x = e.clientX;
		coordsRef.current.mouseStart.y = e.clientY;
		coordsRef.current.mouseStart.time = Date.now();
	};

	const handleScrollend = useCallback(
		(e) => {
			const scrollLeft = e.target.scrollLeft;
			const clientWidth = e.target.clientWidth ?? 0;
			const scrollWidth = e.target.scrollWidth ?? 0;
			if (clientWidth > 0) {
				const pageScrollLength = (scrollWidth - clientWidth) / cursors;
				let progress = Math.round(scrollLeft / pageScrollLength);
				progress = progress < 1 ? 1 : progress;
			}
		},
		[cursors, onEndReached, pagination, cursorRef.current]
	);

	const initSlider = useCallback(() => {
		if (!ref.current.dataset.carousel) {
			ref.current.dataset.carousel = true;
			applyParentStyles();
			applyChildWidth();
		}
	}, [pagination]);


	useLayoutEffect(() => {
		if (ref.current) {
			initSlider();
			ref.current.addEventListener('touchstart', handleTouchStart);
			ref.current.addEventListener('touchend', handleTouchEnd);
			ref.current.addEventListener('mousedown', handleMousedown);
			ref.current.addEventListener('scrollend', handleScrollend);
			return () => {
				ref.current.removeEventListener('touchstart', handleTouchStart);
				ref.current.removeEventListener('touchend', handleTouchEnd);
				ref.current.removeEventListener('mousedown', handleMousedown);
				ref.current.removeEventListener('scrollend', handleScrollend);
			};
		}

		return () => { };
	}, [!!ref.current]);

	useEffect(() => {
		if (ref.current) {
			applyParentStyles();
			applyChildWidth();
		}
	}, [columnGap, pagination, count]);

	return { goBack, goForward, goToPage };
};

export const Carousel = memo(
	({
		rows,
		columns,
		onClickItem,
		noOfitemsPerRow,
		component: Component,
		placeholder: PlaceholderComponent,
		page = 0,
		loading,
		rowProps = { rowSpacing: 1, columnSpacing: { xs: 1, sm: 1, md: 2 }, sx: { padding: 1 } },
		pageCount = 0,
		onEndReached,
		onMouseEnter,
		onMouseLeave,
		itemProps,
		noRecordMsg = strings.NoRecordFound,
		hideIndicators = false,
		...rest
	}) => {
		const theme = useTheme();
		const cursors = useMemo(
			() =>
				Array.from(
					Array(noOfitemsPerRow > 0 && rows?.length > 0 ? Math.ceil(rows.length / noOfitemsPerRow) : 1).keys()
				).map((i) => i + 1),
			[noOfitemsPerRow, rows]
		);

		const [state, setState] = useReducer(
			(prev, next) => (typeof next === 'function' ? { ...prev, ...next(prev) } : { ...prev, ...next }),
			{
				page: 1
			}
		);




		const transitionDuration = {
			enter: theme.transitions.duration.enteringScreen,
			exit: theme.transitions.duration.leavingScreen
		};



		const handleOnChangePage = useCallback(
			(page) => {
				setState({ page: page });
			},
			[pageCount]
		);

		const handleOnEndReached = useCallback(
			(edge) => {
				if (typeof onEndReached === 'function') {
					onEndReached(edge);
				}

			},
			[onEndReached]
		);

		


		const nextFabVisible = state.page < Math.ceil(rows?.length / noOfitemsPerRow);
		const carouselRef = useRef();
		const { goBack, goForward, goToPage } = useCarouselControls(carouselRef, {
			pagination: noOfitemsPerRow,
			count: rows.length,
			columnGap: 10,
			onEndReached: handleOnEndReached,
			onChange: handleOnChangePage
		});

		return (
			<Grid size="grow" {...rest} flexDirection="column" container>
				<Grid
					container
					size="grow"
					spacing={3}
					sx={{
						'& .carousel-items': {
							padding: 0,
							'&::-webkit-scrollbar': {
								width: 0,
								background: 'transparent',
								visibility: 'hidden'
							},
							'& .carousel-item': {
								padding: 0,
								paddingLeft: 0
							}
						},
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<Grid
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
						size="auto"
					>
						{state.page > 1 && (
							<Zoom
								in
								timeout={transitionDuration}
								style={{
									transitionDelay: `${transitionDuration?.exit || 0}ms`
								}}
								unmountOnExit
							>
								<CarouselFab
									onClick={goBack}
									sx={{
										left: theme.spacing(2)
									}}
									size="small"
									color={'primary'}
									disabled={loading}
								>
									<i className={'fa fa-angle-left'} />
								</CarouselFab>
							</Zoom>
						)}
					</Grid>
					<Grid size={10} container>
						<Grid
							columns={noOfitemsPerRow}
							className="carousel-items"
							container
							size={12}
							data-carousel-pagination={noOfitemsPerRow}
							data-carousel-column-gap={10}
							{...rowProps}
							ref={carouselRef}
						>
							{rows?.length > 0 &&
								rows.map((item, index) => (
									<Grid
										size="auto"
										className={'carousel-item'}
										key={`list-item-${index}`}
										onClick={onClickItem}
									>
										<Component
											index={index}
											data={item}
											columns={columns}
											itemProps={itemProps}
											{...rest}
										/>
									</Grid>
								))}
							{!rows?.length && (
								<Grid
									size={12}
								>
									<Box display={'flex'} alignItems={'center'} justifyContent={'center'} padding={4}>
										<Typography color="textDisabled" align="center">
											{noRecordMsg}
										</Typography>
									</Box>
								</Grid>
							)}
						</Grid>
					</Grid>
					<Grid
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
						size="auto"
					>
						{nextFabVisible && (
							<Zoom
								in={nextFabVisible}
								timeout={transitionDuration}
								style={{
									transitionDelay: `${transitionDuration?.exit || 0}ms`
								}}
								unmountOnExit
							>
								<CarouselFab
									sx={{ right: theme.spacing(2) }}
									size="small"
									onClick={goForward}
									color={'primary'}
									disabled={loading}
								>
									<i className={'fa fa-angle-right'} />
								</CarouselFab>
							</Zoom>
						)}
					</Grid>
				</Grid>
				{!loading && cursors.length > 1 && (
					<Grid container>
						<Grid size={12} sx={{ padding: theme.spacing(2) }}>
							<Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
								{cursors.map((cursor, i) => (
									<Bull
										active={cursor === state.page}
										onClick={() => goToPage(cursor)}
										key={`bull-${i}`}
									/>
								))}
							</Stack>
						</Grid>
					</Grid>
				)}
			</Grid>
		);
	},
	(props, nextProps) =>
		util.deepEqual(
			[props.page, props.rows],
			[nextProps.page, nextProps.rows]
		)
);
const GridListBase = ({
	useLinkColumn = false,
	modelConfig,
	apiParams,
	idProperty,
	columns,
	api,
	readOnly,
	defaultSort,
	setActiveRecord,
	onSelection,
	refresh,
	parentFilters,
	parent,
	noOfitemsPerRow = 4,
	mode = 'carousel',
	component: Component = Item,
	placeholder: Placeholder = DefaultPlaceholder,
	fetchData,
	disablePagination,
	disablePlaceHolders,
	itemProps,
	noRecordMsg,
	...others
}) => {
	const [pageSize, setPageSize] = useState(consts.defaultPageSize);
	const [page, setPage] = useState(0);
	const [data, setData] = useState({ rowCount: 0, rows: [] });
	const [isLoading, setIsLoading] = useState(true);
	const licenseData = util.localData?.LicenseAppName;
	const pageCount = data?.rowCount > 0 ? Math.ceil(data.rowCount / pageSize) : 1;
	const sortModel = convertDefaultSort(defaultSort || modelConfig?.defaultSort);
	const filterModel = null;

	const gridColumns = useMemo(() => {
		let allColumns = columns || modelConfig?.getGridColumns(others);
		const finalColumns = [];
		for (const column of allColumns) {
			if (parent && column.lookup === parent) {
				continue;
			}
			if (column.requiredLicense && !licenseData.includes(column.requiredLicense)) {
				continue;
			}
			if (column.disabledLicense && licenseData.includes(column.disabledLicense)) {
				continue;
			}
			finalColumns.push(column);

		}

		return finalColumns;
	}, [columns, modelConfig, parent, licenseData]);



	const handleFetchData = useCallback(() => {
		let dataFetcher = getList;
		if (fetchData) {
			dataFetcher = fetchData;
		}
		dataFetcher({
			page,
			pageSize,
			sortModel,
			filterModel,
			api: api || modelConfig?.endPoint,
			apiParams,
			setIsLoading,
			setData,
			gridColumns,
			modelConfig,
			parentFilters
		});
	}, [fetchData, sortModel, filterModel, parentFilters, page, pageSize, api]);

	const openForm = (id) => {
		getRecord({
			id,
			api: api || modelConfig?.endPoint,
			setIsLoading,
			setActiveRecord,
			apiParams,
			modelConfig,
			parentFilters
		});
	};

	const handleOnItemClick = (event) => {
		const { row: record } = event;
		if (useLinkColumn && event.field === modelConfig.linkColumn && setActiveRecord) {
			openForm(record.id);
		}
		if (onSelection) {
			onSelection(record.id);
		}
	};




	const handleOnChangePage = useCallback((event, newValue) => {
		const nextPageValue = newValue - 1;
		setPage(nextPageValue);
	}, []);
	const handleOnChangePageSize = useCallback((event) => {
		setPageSize(Number(event.target.value));
	}, []);

	const handleOnCarouselEndReached = useCallback((end) => {
		const adjacentPage = end === 'start' ? (page > 0 ? page - 1 : 0) : page < pageCount ? page + 1 : page;
		if (adjacentPage >= 1 && adjacentPage < pageCount) {
			setPage(adjacentPage);
		}
	}, [page, pageCount]);


	useUniqueEffect(() => {
		handleFetchData();
	}, [
		page,
		pageSize,
		sortModel,
		filterModel,
		api,
		refresh,
		gridColumns,
		parentFilters,
		apiParams
	]);
	


	return (
		<Grid container size="grow">
			<Grid container size="grow">
				{!isLoading && (
					<Carousel
						{...others}
						rows={data?.rows || []}
						noOfitemsPerRow={noOfitemsPerRow}
						onClickItem={handleOnItemClick}
						page={page}
						columns={gridColumns}
						component={Component}
						placeholder={Placeholder}
						loading={isLoading}
						rowSpacing={1}
						onEndReached={handleOnCarouselEndReached}
						pageCount={pageCount}
						itemProps={itemProps}
						noRecordMsg={noRecordMsg}
					/>
				)}
				{isLoading && (
					<Grid container size="grow">
						<Grid							
							size={12}
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								padding: 1,
								height: '20vh'
							}}
							columnSpacing={3}
						>
							<CircularProgress size={24} />
						</Grid>
					</Grid>
				)}
			</Grid>
			{!disablePagination && (
				<Grid container size="grow">
					<Grid size={12}>
						<GridListPagination
							rowCount={data?.rowCount || 0}
							page={page}
							pageSize={pageSize}
							pageCount={pageCount}
							onChangePage={handleOnChangePage}
							onChangePageSize={handleOnChangePageSize}
						/>
					</Grid>
				</Grid>
			)}
		</Grid>
	);
};

export default GridListBase;
